import React from 'react';
import { usePWA } from '../../utils/pwaManager.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { CompleteSetupBanner } from './CompleteSetupBanner.jsx';
import { OpenAppBanner } from './OpenAppBanner.jsx';

/**
 * Main PWA Manager Component
 * Orchestrates all PWA-related UI based on current state
 */
export function PWAManager({ className = '' }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const {
    shouldShowSetupBanner,
    shouldShowOpenInAppBanner,
    setupComplete,
    isInStandaloneMode,
    supportsPWA,
  } = usePWA(isAuthenticated);

  // Don't render anything if PWA is not supported, we're in standalone mode, or user is not authenticated
  if (!supportsPWA || isInStandaloneMode || !isAuthenticated) {
    return null;
  }

  // Check if setup banner was dismissed within 24 hours
  const setupBannerDismissed = (() => {
    const dismissedTimestamp = localStorage.getItem('setup-banner-dismissed');
    if (!dismissedTimestamp) return false;

    const dismissedTime = parseInt(dismissedTimestamp);
    const hoursAgo = (Date.now() - dismissedTime) / (1000 * 60 * 60);
    return hoursAgo < 24;
  })();

  return (
    <div className={className}>
      {/* Complete Setup Banner - shows when PWA can be installed or notifications need setup */}
      {shouldShowSetupBanner && !setupBannerDismissed && <CompleteSetupBanner />}

      {/* Open in App Banner - shows when PWA is installed but browsing in web */}
      {shouldShowOpenInAppBanner && <OpenAppBanner />}
    </div>
  );
}

/**
 * PWA Status Component (for debugging/development)
 * Shows current PWA state - remove in production
 */
export function PWAStatus({ className = '' }) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const pwaState = usePWA(isAuthenticated);

  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 max-w-xs rounded-lg bg-black bg-opacity-75 p-3 text-xs text-white ${className}`}
    >
      <h4 className="mb-2 font-bold">PWA Debug Status</h4>
      <div className="space-y-1">
        <div>Authenticated: {isAuthenticated ? '✅' : '❌'}</div>
        <div>Standalone: {pwaState.isInStandaloneMode ? '✅' : '❌'}</div>
        <div>PWA Installed: {pwaState.isPWAInstalled ? '✅' : '❌'}</div>
        <div>Can Install: {pwaState.canInstallPWA ? '✅' : '❌'}</div>
        <div>Notifications: {pwaState.hasNotificationPermission ? '✅' : '❌'}</div>
        <div>Setup Complete: {pwaState.setupComplete ? '✅' : '❌'}</div>
        <div>Show Setup: {pwaState.shouldShowSetupBanner ? '✅' : '❌'}</div>
        <div>Show Open: {pwaState.shouldShowOpenInAppBanner ? '✅' : '❌'}</div>
        <div>Platform: {pwaState.platform}</div>
        <div>Browser: {pwaState.browser}</div>
      </div>
      <div className="mt-2 border-t border-gray-600 pt-2">
        <button
          onClick={() => pwaState.clearInstallationState()}
          className="rounded bg-red-600 px-2 py-1 text-xs hover:bg-red-700"
        >
          Clear State
        </button>
      </div>
    </div>
  );
}

export default PWAManager;
